import pluralize from 'pluralize'

import { ReservationDetails } from '@graphql/queries/reservationDetails'
import {
  ListingType,
  ListingTypeNameEnum,
  PetsRules,
  RestroomAccessType,
  ServerListingDetailsQuery,
} from '@libs/graphql/types'
import { listingAssets } from '@page/listing/assets'
import { listingConstants } from '@page/listing/listing.constants'
import { AdditionalListingDetails } from '@page/listing/listing.gql'
import { getPricingSuffix } from '@page/listing/listing.utils.get-pricing-suffix'
import {
  getGuestRestrictionsDescription,
  getSelectGuestsRestrictionText,
} from '@page/listing/listing.utils.guest-restrictions'
import { Localization } from '@services/localization/localization'

export {
  getGuestRestrictionsDescription,
  getSelectGuestsRestrictionText,
} from '@page/listing/listing.utils.guest-restrictions'

export function openForXForListingType(
  type?: ListingType | ListingTypeNameEnum,
) {
  const {
    [type ?? ListingType.other]: openFor = listingConstants.typeOpenFor[
      ListingType.other
    ],
  } = listingConstants.typeOpenFor

  return openFor
}

export function arenaForListingType(type?: ListingType | ListingTypeNameEnum) {
  const {
    [type ?? ListingType.other]: arena = listingConstants.typeArenas[
      ListingType.other
    ],
  } = listingConstants.typeArenas

  return arena
}

function getParkingSpaceInfo(
  data:
    | Partial<AdditionalListingDetails>
    | { __typename: string; numberOfParkingSpaces?: number },
) {
  let parkingSpaces = 0

  if (!data) return parkingSpaces

  switch (data?.__typename) {
    case 'BasketballCourtListing':
    case 'PickleballCourtListing':
    case 'TennisCourtListing':
    case 'Listing':
    case 'PoolListing':
    case 'HomeListing':
      parkingSpaces = data?.numberOfParkingSpaces ?? 0
      break
  }

  return parkingSpaces
}

type ParkingPartial = {
  parkingOptions?: string[] | null
  parkingContext?: string
  numberOfParkingSpaces?: number
}

export function getParkingCopy(
  data: Partial<AdditionalListingDetails> | ParkingPartial,
) {
  if (data === undefined) {
    return {
      label: '',
      description: '',
      context: '',
      hasParking: false,
      parkingSpaces: 0,
      options: [],
    }
  }

  const parkingSpaces = getParkingSpaceInfo(data)

  const hasParking = parkingSpaces > 0
  const hasOtherOptions = (data?.parkingOptions?.length ?? 0) > 0

  const label = `${hasParking || hasOtherOptions ? '' : 'No '} Parking`

  const availabilityText =
    hasParking || hasOtherOptions
      ? `Up to ${pluralize('car', parkingSpaces ?? 1, true)} on site.`
      : `No parking available on site.`

  let description = availabilityText

  if (hasParking && hasOtherOptions) {
    description += ' More options available.'
  }

  if (!hasParking && hasOtherOptions) {
    description = 'Nearby parking available.'
  }

  return {
    label,
    description,
    availabilityText,
    context: data?.parkingContext,
    hasParking,
    parkingSpaces,
    options: data?.parkingOptions ?? [],
  }
}

type AcceptableListingProp =
  | ReservationDetails['poolPrivate']
  | NonNullable<ServerListingDetailsQuery['listing']>

export function getGuestInfo(
  listing: Partial<AcceptableListingProp>,
  guestAdultAgeLimit = 13,
) {
  if (!listing?.max_guests) return undefined

  const arena = arenaForListingType(listing.listingType?.name ?? undefined)

  const formattedMaxGuests = Intl.NumberFormat(
    Localization().getValuesFromRegion().language,
  ).format(listing.max_guests)

  let description = ''
  let ageLabel = ''

  if (!listing.guests_children_allowed && !listing.guests_infants_allowed) {
    ageLabel = `Must be ${guestAdultAgeLimit} or older`
    description += ageLabel
  } else if (
    listing.guests_children_allowed &&
    !listing.guests_infants_allowed
  ) {
    ageLabel = 'Must be 2 or older'
    description += ageLabel
  } else if (
    !listing.guests_children_allowed &&
    listing.guests_infants_allowed
  ) {
    ageLabel = 'No children (3-12)'
    description += ageLabel
  } else {
    ageLabel = 'All ages welcome'
    description += ageLabel
  }

  const { [listing.pets_rule ?? 'NotAllowed']: petsLabel } = {
    [PetsRules.InSpace]: 'Pets welcome',
    [PetsRules.NotInSpace]: `Pets welcome (not in ${arena})`,
    [PetsRules.NotAllowed]: 'No pets',
  }

  if (listing.pets_rule) {
    description += ` • ${petsLabel}`
  }

  const petsAllowed = listing.pets_rule !== 'NotAllowed' && !!listing.pets_rule
  const kidsAllowed = listing?.guests_children_allowed

  return {
    label: `${formattedMaxGuests} guests allowed`,
    description,
    ageLabel,
    petsLabel,
    petsAllowed,
    kidsAllowed,
    pdpIcon: listingAssets.GuestsAllowedIcon,
    petsIcon: petsAllowed
      ? listingAssets.PetsAllowed
      : listingAssets.PetsNotAllowed,
    kidsIcon: kidsAllowed
      ? listingAssets.KidsAllowed
      : listingAssets.KidsNotAllowed,
  }
}

export function getRestroomInfo(data: Partial<AdditionalListingDetails>) {
  if (!data) return undefined
  const restroomType = data?.restroomAccessType

  const hasRestroomAccess =
    !!restroomType && restroomType !== RestroomAccessType.NoRestroom

  const arena = arenaForListingType(data?.listingType?.name)

  const {
    [restroomType ?? RestroomAccessType.NoRestroom]: description,
  }: Record<string, string | undefined> = {
    [RestroomAccessType.ResidenceRestroom]:
      'Private restroom available inside main home',
    [RestroomAccessType.PrivateRestroom]: `Dedicated restroom available by the ${arena}`,
    [RestroomAccessType.PortableRestroom]: `Dedicated portable restroom available by the ${arena}`,
    [RestroomAccessType.NoRestroom]: undefined,
  }

  const {
    [restroomType ?? RestroomAccessType.NoRestroom]: shortDesription,
  }: Record<string, string | undefined> = {
    [RestroomAccessType.ResidenceRestroom]: 'Private restroom',
    [RestroomAccessType.PrivateRestroom]: `Dedicated restroom`,
    [RestroomAccessType.PortableRestroom]: `Portable restroom`,
    [RestroomAccessType.NoRestroom]: undefined,
  }

  const listingLabel = hasRestroomAccess
    ? 'Restroom available'
    : 'No restroom available'

  return {
    pdpIcon: listingAssets.RestroomIcon,
    icon: hasRestroomAccess
      ? listingAssets.RestroomAvailable
      : listingAssets.RestroomNotAvailable,
    label: listingLabel,
    description: description,
    shortDesription: shortDesription,
    hasRestroomAccess,
  }
}

export function verbForListingType(type?: ListingType | ListingTypeNameEnum) {
  const {
    [type ?? ListingType.other]: verb = listingConstants.typeVerbs[
      ListingType.other
    ],
  } = listingConstants.typeVerbs

  return verb
}

export const listingUtils = {
  arenaForListingType,
  verbForListingType,
  getPricingSuffix,
  getGuestRestrictionsDescription,
  getSelectGuestsRestrictionText,
}
